import type { ChangeEvent, FocusEvent, InputHTMLAttributes, MouseEvent } from 'react';
import { useFieldControl } from '../Field/fieldContext';
import styles from './Switch.module.css';

type NativeSwitchProps = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'className' | 'disabled' | 'type' | 'role' | 'size' | 'children'
>;

export interface SwitchProps extends NativeSwitchProps {
    /**
     * The visible label, and §5 is specific about what it says: **what being ON means**, never just
     * the field name. "Email me when a sample is approved" — not "Notifications". A user flipping a
     * switch has to know what they are turning on without flipping it to find out.
     */
    readonly label: string;
    /** Rendered as `aria-disabled` plus a suppressed toggle — see the component body. */
    readonly disabled?: boolean;
    readonly 'data-testid'?: string;
}

/**
 * A real `<input type="checkbox" role="switch">` (FS-3 FR-C2) wearing §5's Switch chrome — pill track,
 * `accent/gold` when on, `surface/card` thumb, `motion/fast`.
 *
 * **A switch means the change has already taken effect.** §5 states the consequence as a rule:
 * inside a form with a Save button, use `Checkbox` instead. A switch there tells the user their
 * change is live when it is sitting unsaved in component state, and the two are indistinguishable
 * on screen — which is why the design system makes it a choice of component rather than a note. It
 * cannot be enforced from here (this component cannot see whether it is inside a form with a Save
 * button), so it is written where the person choosing between the two will read it.
 *
 * `role="switch"` rather than a plain checkbox role is what makes a screen reader say "on"/"off"
 * instead of "checked"/"unchecked", which is the difference between a setting and a tick box. It is
 * not overridable: a switch that announced itself as a checkbox would be neither.
 *
 * **Disabled is `aria-disabled` plus a suppressed toggle, never the bare attribute** — see the same
 * note on Checkbox.
 */
export function Switch({
    label,
    disabled = false,
    id,
    'aria-describedby': describedBy,
    'data-testid': testId,
    onBlur,
    onChange,
    onClick,
    ...rest
}: SwitchProps) {
    // No `invalid` and no `required`: a setting that applies immediately has nothing to validate
    // and nothing to submit, so an invalid switch would be a state with no way to reach it. A
    // toggle that DOES need those is a checkbox in a form — which is §5's rule above, and this
    // omission is what makes the API say so.
    const field = useFieldControl({ id, 'aria-describedby': describedBy });

    function handleBlur(event: FocusEvent<HTMLInputElement>) {
        onBlur?.(event);
        field.reportBlur(event.target.checked ? 'true' : '');
    }

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        if (disabled) {
            // BOTH guards are needed, and this is the non-obvious one. React synthesises `onChange`
            // for checkbox-shaped inputs from the CLICK event, and it does so whether or not the
            // click was prevented — so `handleClick` alone stops the toggle on screen while the
            // caller's handler still fires. A caller written as `onChange={() => setOn((v) => !v)}`
            // would then flip its own state on a control the user is not allowed to operate.
            return;
        }
        onChange?.(event);
        field.reportChange(event.target.checked ? 'true' : '');
    }

    function handleClick(event: MouseEvent<HTMLInputElement>) {
        if (disabled) {
            event.preventDefault();
            return;
        }
        onClick?.(event);
    }

    return (
        <label className={styles.row}>
            <span className={styles.track}>
                <input
                    {...rest}
                    aria-describedby={field['aria-describedby']}
                    aria-disabled={disabled}
                    className={styles.input}
                    data-testid={testId}
                    id={field.id}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onClick={handleClick}
                    // `role="switch"` on a real checkbox takes its aria-checked from the element's
                    // own `checked` state — that mapping is HTML-AAM's, not something this component
                    // supplies. Writing aria-checked by hand would be worse than the lint it
                    // silences: the switch may be uncontrolled (`defaultChecked`), in which case
                    // React does not know the current value and the attribute would announce a
                    // stale one, overriding the native mapping that was already correct.
                    // biome-ignore lint/a11y/useAriaPropsForRole: see the note directly above
                    role="switch"
                    type="checkbox"
                />
                {/* Decorative: the thumb draws what `checked` already announces. Its position comes
                    from the input's own :checked state so the two cannot get out of step. */}
                <span className={styles.thumb} />
            </span>
            <span className={styles.label}>{label}</span>
        </label>
    );
}
